import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Rating,
  TablePagination,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const Testimonials = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/testimonials/admin', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setTestimonials(data);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      setError('Не удалось загрузить отзывы. Пожалуйста, попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/testimonials/${selectedTestimonial.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setTestimonials(testimonials.filter(t => t.id !== selectedTestimonial.id));
      setSuccessMessage('Отзыв успешно удален');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting testimonial:', err);
      setError('Не удалось удалить отзыв');
      setTimeout(() => setError(null), 3000);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedTestimonial(null);
    }
  };

  const handleStatusChange = async (testimonial, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`/api/testimonials/${testimonial.id}/status`, 
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      console.log('Ответ сервера при изменении статуса:', response.data);
      
      // Используем данные, возвращаемые с сервера
      if (response.data && response.data.success) {
        const updatedTestimonial = response.data.data;
        
        setTestimonials(testimonials.map(t => 
          t.id === testimonial.id ? updatedTestimonial : t
        ));
        
        setSuccessMessage(`Статус отзыва изменен на "${newStatus === 'approved' ? 'Одобрен' : 'Отклонен'}"`);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Error updating testimonial status:', err);
      setError('Не удалось обновить статус отзыва');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleToggleFeatured = async (testimonial) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`/api/testimonials/${testimonial.id}/featured`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('Ответ сервера при переключении статуса избранного:', response.data);
      
      // Используем данные, возвращаемые с сервера
      if (response.data && response.data.success) {
        const updatedTestimonial = response.data.data;
        
        setTestimonials(testimonials.map(t => 
          t.id === testimonial.id ? updatedTestimonial : t
        ));
        
        setSuccessMessage(`Отзыв ${!testimonial.featured ? 'добавлен в' : 'удален из'} избранное`);
      } else {
        throw new Error('Сервер вернул некорректные данные');
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error toggling featured status:', err);
      setError('Не удалось изменить статус избранного отзыва');
      setTimeout(() => setError(null), 3000);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'd MMMM yyyy', { locale: ru });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'approved':
        return <Chip label="Одобрен" color="success" size="small" />;
      case 'rejected':
        return <Chip label="Отклонен" color="error" size="small" />;
      case 'pending':
      default:
        return <Chip label="На модерации" color="warning" size="small" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
      >
        Управление отзывами
      </Typography>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: { xs: 1, sm: 2 } }}>
        <TableContainer sx={{ 
          maxHeight: 600,
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: 8
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.primary.light,
            borderRadius: 4
          }
        }}>
          <Table stickyHeader aria-label="sticky table" size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 } }}>Пользователь</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 } }}>Дата</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 } }}>Рейтинг</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>Объект</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>Текст отзыва</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 } }}>Статус</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>Избранное</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 } }}>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testimonials
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((testimonial) => (
                  <TableRow hover key={testimonial.id}>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                      <Typography noWrap variant="body2">
                        {testimonial.user?.name || 'Неизвестно'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                      <Typography noWrap variant="body2">
                        {formatDate(testimonial.date)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                      <Rating value={testimonial.rating} readOnly size={isMobile ? "small" : "medium"} />
                    </TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                      {testimonial.property ? (
                        <Typography variant="body2" color="primary" noWrap>
                          {testimonial.property.name}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary" noWrap>
                          Не указан
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2" sx={{ 
                        maxWidth: { xs: 150, sm: 200, md: 300 }, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap' 
                      }}>
                        {testimonial.text}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                      {getStatusChip(testimonial.status)}
                    </TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>
                      <IconButton 
                        size="small" 
                        color={testimonial.featured ? "primary" : "default"}
                        onClick={() => handleToggleFeatured(testimonial)}
                      >
                        {testimonial.featured ? <StarIcon /> : <StarBorderIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex' }}>
                        {testimonial.status === 'pending' && (
                          <>
                            <IconButton 
                              color="success" 
                              size="small"
                              onClick={() => handleStatusChange(testimonial, 'approved')}
                              title="Одобрить"
                            >
                              <CheckIcon />
                            </IconButton>
                            <IconButton 
                              color="error" 
                              size="small"
                              onClick={() => handleStatusChange(testimonial, 'rejected')}
                              title="Отклонить"
                            >
                              <CloseIcon />
                            </IconButton>
                          </>
                        )}
                        
                        <IconButton 
                          color="error" 
                          size="small"
                          onClick={() => handleDeleteClick(testimonial)}
                          title="Удалить"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              
              {testimonials.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      Отзывы отсутствуют
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={testimonials.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
        />
      </Paper>
      
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить этот отзыв? Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Testimonials;
